import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface LiveMetric {
  id: string
  name: string
  current_value: number
  previous_value: number
  change_percent: number
  unit: string
  trend: string
  last_updated: string
}

interface ActiveSession {
  id: string
  user_id: string
  location: string
  device: string
  current_page: string
  duration_seconds: number
  page_views: number
  referrer: string
}

interface Statistics {
  active_users: number
  active_sessions: number
  page_views_last_hour: number
  avg_session_duration_sec: number
  bounce_rate: number
  conversion_rate: number
  revenue_last_hour: number
  events_per_second: number
}

export default function RealTimeAnalyticsPage() {
  const [liveMetrics, setLiveMetrics] = useState<LiveMetric[]>([])
  const [activeSessions, setActiveSessions] = useState<ActiveSession[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [activeTab, setActiveTab] = useState<'metrics' | 'sessions'>('metrics')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
    const interval = setInterval(fetchData, 30000) // Refresh every 30 seconds
    return () => clearInterval(interval)
  }, [])

  const fetchData = async () => {
    try {
      const [metricsRes, sessionsRes, statsRes] = await Promise.all([
        fetch('/api/realtimeanalytics/metrics'),
        fetch('/api/realtimeanalytics/sessions'),
        fetch('/api/realtimeanalytics/statistics')
      ])

      if (metricsRes.ok) setLiveMetrics(await metricsRes.json())
      if (sessionsRes.ok) setActiveSessions(await sessionsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching real-time analytics data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return '↑'
      case 'down': return '↓'
      default: return '→'
    }
  }

  const getTrendColor = (trend: string, change: number) => {
    if (trend === 'up' && change > 0) return 'text-green-600'
    if (trend === 'down' && change < 0) return 'text-red-600'
    return 'text-gray-600'
  }

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}m ${secs}s`
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Real-Time Analytics</h1>
            <p className="mt-2 text-gray-600">Live metrics and active session monitoring</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
            <span className="text-sm text-green-600 font-medium">Live</span>
          </div>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
              <p className="text-sm text-gray-500">Active Users</p>
              <p className="text-3xl font-bold text-gray-900">{statistics.active_users.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
              <p className="text-sm text-gray-500">Page Views/Hour</p>
              <p className="text-3xl font-bold text-blue-600">{statistics.page_views_last_hour.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6 border-l-4 border-purple-500">
              <p className="text-sm text-gray-500">Conversion Rate</p>
              <p className="text-3xl font-bold text-purple-600">{(statistics.conversion_rate * 100).toFixed(2)}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6 border-l-4 border-orange-500">
              <p className="text-sm text-gray-500">Revenue/Hour</p>
              <p className="text-3xl font-bold text-orange-600">${statistics.revenue_last_hour.toLocaleString()}</p>
            </div>
          </div>
        )}

        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('metrics')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'metrics' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'
              }`}
            >
              Live Metrics ({liveMetrics.length})
            </button>
            <button
              onClick={() => setActiveTab('sessions')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'sessions' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'
              }`}
            >
              Active Sessions ({activeSessions.length})
            </button>
          </nav>
        </div>

        {activeTab === 'metrics' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {liveMetrics.map((metric) => (
              <div key={metric.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-sm font-medium text-gray-500">{metric.name}</h3>
                  <span className={`text-lg ${getTrendColor(metric.trend, metric.change_percent)}`}>
                    {getTrendIcon(metric.trend)}
                  </span>
                </div>
                <div className="flex items-end gap-3">
                  <p className="text-3xl font-bold text-gray-900">
                    {metric.unit === 'currency' ? '$' : ''}{metric.current_value.toLocaleString()}{metric.unit === 'percent' ? '%' : ''}
                  </p>
                  <p className={`text-sm font-medium ${getTrendColor(metric.trend, metric.change_percent)}`}>
                    {metric.change_percent >= 0 ? '+' : ''}{metric.change_percent.toFixed(1)}%
                  </p>
                </div>
                <p className="text-xs text-gray-400 mt-2">vs {metric.previous_value.toLocaleString()} previous</p>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'sessions' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Device</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current Page</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pages</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referrer</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {activeSessions.slice(0, 15).map((session) => (
                  <tr key={session.id}>
                    <td className="px-6 py-4 text-gray-900">{session.location}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        session.device === 'desktop' ? 'bg-blue-100 text-blue-800' :
                        session.device === 'mobile' ? 'bg-green-100 text-green-800' :
                        'bg-purple-100 text-purple-800'
                      }`}>
                        {session.device}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-500 truncate max-w-xs">{session.current_page}</td>
                    <td className="px-6 py-4 text-gray-500">{formatDuration(session.duration_seconds)}</td>
                    <td className="px-6 py-4 text-gray-500">{session.page_views}</td>
                    <td className="px-6 py-4 text-gray-500 truncate max-w-xs capitalize">{session.referrer.replace('_', ' ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  )
}
